
<script setup>
import { watch, onBeforeMount } from 'vue';

import { useBaseSkillsAdmin } from '@/composables/useBaseSkillsAdmin';
import { useSkillsAiWorkflows } from '@/composables/useSkillsAiWorkflows';
import { useNotification } from '@/composables/useNotification';

import Notification from '@/components/common/SnackBar.vue';
import Pagination from '@/components/cPagination.vue';
import PageDescription from '@/components/cPageDescription.vue';

const { notification, notify } = useNotification();
const admin = useBaseSkillsAdmin();
const workflows = useSkillsAiWorkflows();

// --- Handlers (thin wrappers passing notify) ---
function handleSearch() {
	admin.onSearch(notify);
}

function handleFilterChange() {
	admin.onFilterChange(notify);
}

function handleClearSearch() {
	admin.searchText.value = '';
	admin.onSearch(notify);
}

async function handleToggleBaseSkill(item) {
	await admin.toggleBaseSkill(item, notify);
}

async function handleBulkSetBase(val) {
	await admin.bulkSetBase(val, notify);
}

async function handleOpenDetail(item) {
	await admin.openDetailDialog(item, notify);
}

async function handleRefresh() {
	await admin.fetchData(notify);
}

async function handleExport() {
	await admin.exportToExcel(notify);
}

async function handleOpenAiWorkflows() {
	await workflows.openPanel([...admin.selected.value], notify);
}

function handleChainChange() {
	workflows.onChainChange();
}

async function handleExecuteChain() {
	await workflows.executeChain(notify);
}

function formatElapsed(seconds) {
	if (seconds < 60) return `${seconds}s`;
	const m = Math.floor(seconds / 60);
	const s = seconds % 60;
	return `${m}m ${s}s`;
}

// --- Watchers ---
watch([admin.currentPage, admin.pageSize], () => {
	admin.fetchData(notify);
});

watch([admin.sortBy, admin.sortDesc], () => {
	admin.currentPage.value = 1;
	admin.fetchData(notify);
});

watch(workflows.executionState, (state) => {
	if (state === 'completed') {
		admin.fetchData(notify);
	}
});

// --- Lifecycle ---
onBeforeMount(() => {
	admin.fetchProviders(notify);
	admin.fetchData(notify);
});
</script>

<template>
	<div class="base-skills-admin">
		<Notification :notification="notification" />

		<div class="page-header pa-4 pb-0">
			<div class="d-flex align-center mb-4">
				<h2>Skills Framework Admin</h2>
				<v-spacer />
				<v-btn small outlined :loading="admin.loading.value" class="mr-2" @click="handleRefresh">
					<v-icon left small>mdi-refresh</v-icon>
					Refresh
				</v-btn>
				<v-btn small outlined :loading="admin.exporting.value" @click="handleExport">
					<v-icon left small>mdi-file-excel-outline</v-icon>
					Export Excel
				</v-btn>
			</div>

			<!-- Filters -->
			<v-row dense class="mb-2" align="center">
				<v-col cols="auto" style="min-width: 200px;">
					<v-text-field v-model="admin.searchText.value" label="Search name..." outlined dense hide-details clearable
						@keydown.enter="handleSearch" @click:clear="handleClearSearch">
						<template #append>
							<v-icon @click="handleSearch" small>mdi-magnify</v-icon>
						</template>
					</v-text-field>
				</v-col>
				<v-col cols="auto" style="min-width: 140px;">
					<v-select v-model="admin.selectedProvider.value" :items="admin.providers.value" item-text="framework_name"
						item-value="framework_provider_id" label="Provider" outlined dense hide-details clearable
						@change="handleFilterChange" />
				</v-col>
				<v-col cols="auto" style="min-width: 160px;">
					<v-select v-model="admin.selectedType.value" :items="admin.types.value" label="Type" outlined dense hide-details clearable
						multiple small-chips deletable-chips @change="handleFilterChange" />
				</v-col>
				<v-col cols="auto" style="min-width: 130px;">
					<v-select v-model="admin.selectedBaseStatus.value" :items="[{ text: 'Base Only', value: 1 }, { text: 'Non-Base', value: 0 }]"
						item-text="text" item-value="value" label="Base Status" outlined dense hide-details clearable
						@change="handleFilterChange" />
				</v-col>
				<v-col cols="auto" style="min-width: 130px;">
					<v-select v-model="admin.selectedDescriptorStatus.value" :items="[{ text: 'Has Desc.', value: 1 }, { text: 'No Desc.', value: 0 }]"
						item-text="text" item-value="value" label="Descriptors" outlined dense hide-details clearable
						@change="handleFilterChange" />
				</v-col>
				<v-col cols="auto" style="min-width: 130px;">
					<v-select v-model="admin.selectedProficiencyStatus.value" :items="[{ text: 'Has Prof.', value: 1 }, { text: 'No Prof.', value: 0 }]"
						item-text="text" item-value="value" label="Proficiencies" outlined dense hide-details clearable
						@change="handleFilterChange" />
				</v-col>
				<v-spacer />
				<v-col cols="auto">
					<v-btn small color="deep-purple" dark :disabled="!admin.hasSelection.value" class="mr-2" @click="handleOpenAiWorkflows">
						<v-icon left small>mdi-robot-outline</v-icon>
						AI Workflows ({{ admin.selectionCount.value }})
					</v-btn>
					<v-btn small color="primary" :disabled="!admin.hasSelection.value" class="mr-2" @click="handleBulkSetBase(1)">
						Set as Base ({{ admin.selectionCount.value }})
					</v-btn>
					<v-btn small outlined :disabled="!admin.hasSelection.value" @click="handleBulkSetBase(0)">
						Unset Base ({{ admin.selectionCount.value }})
					</v-btn>
				</v-col>
			</v-row>
		</div>

		<div class="page-body">
			<!-- Main Content -->
			<div class="main-content">
				<div class="table-scroll">
					<!-- Data Table -->
					<v-data-table v-model="admin.selected.value" :headers="admin.headers" :items="admin.items.value" :loading="admin.loading.value"
						:server-items-length="admin.totalItems.value" show-select item-key="framework_provider_data_id"
						hide-default-footer :sort-by.sync="admin.sortBy.value" :sort-desc.sync="admin.sortDesc.value" :items-per-page="admin.pageSize.value"
						@update:sort-by="admin.onSort" dense>
						<template v-slot:[`item.descriptor_langs`]="{ item }">
							<span v-if="!item.descriptor_langs" class="grey--text text--lighten-1">—</span>
							<div v-else class="d-flex flex-wrap align-center" style="gap: 2px;">
								<v-chip v-for="lang in admin.parseLangs(item.descriptor_langs)" :key="lang" x-small class="clickable-chip"
									color="blue lighten-4" @click="handleOpenDetail(item)">{{ lang }}</v-chip>
							</div>
						</template>
						<template v-slot:[`item.proficiency_langs`]="{ item }">
							<span v-if="!item.proficiency_langs" class="grey--text text--lighten-1">—</span>
							<div v-else class="d-flex flex-wrap align-center" style="gap: 2px;">
								<v-chip v-for="lang in admin.parseLangs(item.proficiency_langs)" :key="lang" x-small class="clickable-chip"
									color="green lighten-4" @click="handleOpenDetail(item)">{{ lang }}</v-chip>
							</div>
						</template>
						<template v-slot:[`item.base_client_skill`]="{ item }">
							<v-switch :input-value="!!item.base_client_skill" dense hide-details class="mt-0 pt-0"
								@change="handleToggleBaseSkill(item)" />
						</template>
					</v-data-table>
				</div>

				<!-- Footer (pinned to bottom) -->
				<div class="table-footer">
					<v-row dense align="center" class="px-4">
						<v-col cols="auto">
							<PageDescription :totalItems="admin.totalItems.value" :pageSize="admin.pageSize.value" :currentPage="admin.currentPage.value" />
						</v-col>
						<v-spacer />
						<v-col cols="auto">
							<Pagination :totalItems="admin.totalItems.value" :pageSize="admin.pageSize.value" :currentPage="admin.currentPage.value"
								@pageNavigation="admin.onPageNav" />
						</v-col>
						<v-col cols="auto">
							<v-select v-model="admin.pageSize.value" :items="[25, 50, 100, 200]" label="Per page" outlined dense hide-details
								style="max-width: 100px;" />
						</v-col>
					</v-row>
				</div>
			</div>

			<!-- AI Workflows Side Panel (fixed, not a flyout) -->
			<div v-if="workflows.panelOpen.value" class="ai-workflows-panel">
				<div class="panel-inner">
					<div class="d-flex align-center mb-4">
						<v-icon class="mr-2" color="deep-purple">mdi-robot-outline</v-icon>
						<h3 class="ma-0">AI Workflows</h3>
						<v-spacer />
						<v-btn icon small @click="workflows.closePanel()">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</div>

					<div v-if="workflows.loading.value" class="text-center pa-6">
						<v-progress-circular indeterminate color="deep-purple" />
						<div class="mt-2 body-2 grey--text">Loading available chains...</div>
					</div>

					<template v-else>
						<!-- === IDLE: Configuration & Payload === -->
						<template v-if="workflows.executionState.value === 'idle'">
							<div class="mb-4">
								<div class="subtitle-2 mb-1">Selected Skills</div>
								<v-chip small color="deep-purple" dark>
									{{ workflows.selectedSkills.value.length }} skill(s)
								</v-chip>
							</div>

							<v-select v-model="workflows.selectedChainId.value" :items="workflows.chains.value"
								item-text="chain_name" item-value="prompt_chain_id" label="Prompt Chain" outlined dense
								hide-details class="mb-3" @change="handleChainChange">
								<template v-slot:item="{ item }">
									<v-list-item-content>
										<v-list-item-title>{{ item.chain_name }}</v-list-item-title>
										<v-list-item-subtitle>
											{{ item.description || 'No description' }}
											<v-chip x-small class="ml-1" :color="item.is_system ? 'orange lighten-4' : 'blue lighten-4'">
												{{ item.is_system ? 'System' : 'Client' }}
											</v-chip>
											<v-chip x-small class="ml-1">{{ item.step_count }} steps</v-chip>
										</v-list-item-subtitle>
									</v-list-item-content>
								</template>
							</v-select>

							<div v-if="workflows.selectedChain.value" class="mb-3">
								<div class="body-2 grey--text">{{ workflows.selectedChain.value.description }}</div>
							</div>

							<div v-if="workflows.inputSchemaInputs.value.length" class="mb-3">
								<div class="subtitle-2 mb-1">Input Schema</div>
								<div v-for="input in workflows.inputSchemaInputs.value" :key="input.key" class="d-flex align-center mb-1">
									<v-chip x-small outlined class="mr-2">{{ input.data_type || 'any' }}</v-chip>
									<span class="body-2">{{ input.label || input.key }}</span>
									<v-chip v-if="input.required" x-small color="red lighten-4" class="ml-1">required</v-chip>
								</div>
							</div>

							<v-divider class="mb-3" />

							<div class="subtitle-2 mb-1">Payload Preview</div>
							<div v-if="workflows.formattedPayload.value" class="payload-container mb-4">
								<div class="caption grey--text mb-1">
									Input key: <code>{{ workflows.formattedPayload.value.inputKey }}</code>
									&mdash; {{ workflows.formattedPayload.value.items.length }} item(s)
								</div>
								<pre class="payload-pre">{{ JSON.stringify(workflows.formattedPayload.value.payload, null, 2) }}</pre>
							</div>
							<div v-else class="body-2 grey--text mb-4">No payload generated</div>

							<v-btn color="deep-purple" dark block :disabled="!workflows.formattedPayload.value" @click="handleExecuteChain">
								<v-icon left>mdi-play</v-icon>
								Execute Chain
							</v-btn>
						</template>

						<!-- === EXECUTING: Progress Display === -->
						<template v-else-if="workflows.executionState.value === 'executing'">
							<div class="execution-progress">
								<v-progress-circular
									v-if="!workflows.executionProgress.value"
									indeterminate color="deep-purple" size="72" width="6"
								/>
								<v-progress-circular
									v-else
									:value="workflows.executionProgress.value.progress_percentage"
									color="deep-purple" size="72" width="6"
								>
									<span class="body-2 font-weight-medium">{{ workflows.executionProgress.value.progress_percentage }}%</span>
								</v-progress-circular>

								<div class="mt-4 subtitle-1">{{ workflows.executionCurrentStep.value || 'Processing...' }}</div>

								<div v-if="workflows.executionProgress.value" class="mt-3" style="width: 100%; max-width: 300px;">
									<v-progress-linear
										:value="(workflows.executionProgress.value.completed_steps / workflows.executionProgress.value.total_steps) * 100"
										color="deep-purple" height="6" rounded
									/>
									<div class="caption grey--text mt-1 text-center">
										{{ workflows.executionProgress.value.completed_steps }}/{{ workflows.executionProgress.value.total_steps }} steps
									</div>
								</div>

								<div class="mt-3 caption grey--text">
									<v-icon x-small class="mr-1">mdi-timer-outline</v-icon>
									{{ formatElapsed(workflows.elapsedSeconds.value) }} elapsed
								</div>
							</div>
						</template>

						<!-- === COMPLETED: Results Display === -->
						<template v-else-if="workflows.executionState.value === 'completed'">
							<div class="execution-result">
								<div class="d-flex align-center mb-2">
									<v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
									<span class="subtitle-1 font-weight-medium">Execution Complete</span>
								</div>

								<div v-if="workflows.executionResult.value?.executionDetails" class="body-2 grey--text mb-3">
									{{ workflows.executionResult.value.executionDetails.chain_name }}
									&middot; {{ workflows.executionResult.value.executionDetails.completed_steps }}/{{ workflows.executionResult.value.executionDetails.total_steps }} steps
									<span v-if="workflows.executionResult.value.executionDetails.processing_time">
										&middot; {{ formatElapsed(Math.round(workflows.executionResult.value.executionDetails.processing_time / 1000)) }}
									</span>
								</div>

								<!-- Skill Cards -->
								<template v-if="workflows.parsedSkillResults.value.length">
									<div class="subtitle-2 mb-2">{{ workflows.parsedSkillResults.value.length }} Skill(s) Generated</div>

									<div v-for="(skill, idx) in workflows.parsedSkillResults.value" :key="idx" class="skill-result-card">
										<div class="skill-result-header">
											<div class="skill-header-main">
												<div class="skill-title">{{ skill.Description || skill.name || skill.Id }}</div>
												<span v-if="skill.Type" class="skill-type-badge" :class="`type-${(skill.Type || '').toLowerCase()}`">{{ skill.Type }}</span>
											</div>
											<div class="skill-meta">
												<span v-if="skill.Id" class="skill-id">{{ skill.Id }}</span>
											</div>
										</div>

										<div v-if="workflows.getSkillDescriptor(skill)" class="skill-section">
											<div class="section-title">Description</div>
											<p class="descriptor-text">{{ workflows.getSkillDescriptor(skill) }}</p>
										</div>

										<div v-if="workflows.getSkillProficiencies(skill).length" class="skill-section">
											<div class="section-title">Proficiency Levels</div>
											<div class="proficiency-grid">
												<div v-for="(level, lidx) in workflows.getSkillProficiencies(skill)" :key="lidx" class="proficiency-item">
													<span class="proficiency-badge" :class="`level-${lidx}`">{{ level.proficiency || level.name }}</span>
													<p class="proficiency-description">{{ level.descriptor || level.description || '' }}</p>
												</div>
											</div>
										</div>
									</div>
								</template>

								<!-- Fallback: raw JSON output -->
								<template v-else>
									<div class="subtitle-2 mb-1">Raw Output</div>
									<div class="payload-container mb-4">
										<pre class="payload-pre">{{ JSON.stringify(workflows.executionResult.value?.outputs || {}, null, 2) }}</pre>
									</div>
								</template>

								<v-btn outlined color="deep-purple" block class="mt-3" @click="workflows.resetExecution()">
									<v-icon left>mdi-refresh</v-icon>
									Run Again
								</v-btn>
							</div>
						</template>

						<!-- === FAILED: Error Display === -->
						<template v-else-if="workflows.executionState.value === 'failed'">
							<div class="execution-error">
								<div class="d-flex align-center mb-2">
									<v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
									<span class="subtitle-1 font-weight-medium">Execution Failed</span>
								</div>

								<v-alert type="error" dense outlined class="mb-4 body-2">
									{{ workflows.executionError.value }}
								</v-alert>

								<div class="d-flex">
									<v-btn color="deep-purple" dark class="mr-2" @click="handleExecuteChain">
										<v-icon left>mdi-refresh</v-icon>
										Retry
									</v-btn>
									<v-btn outlined @click="workflows.resetExecution()">
										<v-icon left>mdi-arrow-left</v-icon>
										Back
									</v-btn>
								</div>
							</div>
						</template>
					</template>
				</div>
			</div>
		</div>

		<!-- Detail Dialog -->
		<v-dialog v-model="admin.detailDialog.show" max-width="700" scrollable>
			<v-card>
				<v-card-title class="d-flex align-center">
					<span>{{ admin.detailDialog.item?.name }}</span>
					<v-spacer />
					<v-chip x-small class="ml-2">{{ admin.detailDialog.item?.type }}</v-chip>
					<v-chip x-small class="ml-1">{{ admin.detailDialog.item?.external_id }}</v-chip>
				</v-card-title>
				<v-card-text v-if="admin.detailDialog.loading" class="text-center pa-6">
					<v-progress-circular indeterminate color="primary" />
				</v-card-text>
				<v-card-text v-else>
					<div v-if="admin.detailDialog.descriptors.length" class="mb-4">
						<div class="subtitle-2 mb-2">Descriptors</div>
						<div v-for="d in admin.detailDialog.descriptors" :key="d.language_code" class="mb-2">
							<v-chip x-small color="blue lighten-4" class="mr-2">{{ d.language_code }}</v-chip>
							<strong v-if="d.name">{{ d.name }}</strong>
							<div v-if="d.descriptor" class="ml-8 mt-1 body-2">{{ d.descriptor }}</div>
						</div>
					</div>
					<div v-else class="mb-4 grey--text">No descriptors</div>

					<div v-if="admin.detailDialog.proficiencies.length">
						<div class="subtitle-2 mb-2">Proficiency Descriptors</div>
						<div v-for="(profs, lang) in admin.groupProficienciesByLang(admin.detailDialog.proficiencies)" :key="lang" class="mb-3">
							<v-chip x-small color="green lighten-4" class="mb-1">{{ lang }}</v-chip>
							<v-simple-table dense class="ml-2">
								<tbody>
									<tr v-for="p in profs" :key="p.proficiency">
										<td style="width: 120px; padding: 4px 8px;">
											<span class="proficiency-badge" :class="`level-${admin.proficiencyLevelIndex(p.proficiency)}`">{{ p.proficiency }}</span>
										</td>
										<td class="proficiency-description" style="padding: 4px 8px;">{{ p.descriptor }}</td>
									</tr>
								</tbody>
							</v-simple-table>
						</div>
					</div>
					<div v-else class="grey--text">No proficiency descriptors</div>
				</v-card-text>
				<v-card-actions>
					<v-spacer />
					<v-btn text @click="admin.closeDetailDialog">Close</v-btn>
				</v-card-actions>
			</v-card>
		</v-dialog>
	</div>
</template>

<style scoped>
.base-skills-admin {
	width: 100%;
	height: calc(100vh - 48px);
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
.page-header {
	flex-shrink: 0;
}
.page-body {
	flex: 1;
	display: flex;
	overflow: hidden;
	min-height: 0;
}
.main-content {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-width: 0;
	overflow: hidden;
}
.table-scroll {
	flex: 1;
	overflow-y: auto;
	padding: 0 16px;
	min-height: 0;
}
.table-footer {
	flex-shrink: 0;
	border-top: 1px solid #e0e0e0;
	padding: 15px 0;
}
.ai-workflows-panel {
	width: 520px;
	flex-shrink: 0;
	border-left: 1px solid #e0e0e0;
	background: #fafafa;
	overflow-y: auto;
}
.panel-inner {
	padding: 16px;
}
.clickable-chip {
	cursor: pointer;
}
.payload-container {
	background: #f5f5f5;
	border: 1px solid #e0e0e0;
	border-radius: 4px;
	padding: 12px;
	max-height: 400px;
	overflow: auto;
}
.payload-pre {
	margin: 0;
	white-space: pre-wrap;
	word-break: break-word;
	font-size: 12px;
	line-height: 1.4;
}
.execution-progress {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 40px 20px;
	text-align: center;
}
.execution-result,
.execution-error {
	padding: 8px 0;
}

/* Skill Result Cards */
.skill-result-card {
	background: #fff;
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	padding: 14px;
	margin-bottom: 12px;
	transition: box-shadow 0.2s;
}
.skill-result-card:hover {
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.skill-result-header {
	margin-bottom: 10px;
}
.skill-header-main {
	display: flex;
	align-items: center;
	gap: 8px;
	margin-bottom: 4px;
}
.skill-title {
	font-weight: 600;
	font-size: 14px;
	line-height: 1.3;
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
}
.skill-type-badge {
	display: inline-block;
	font-size: 11px;
	font-weight: 500;
	padding: 1px 8px;
	border-radius: 10px;
	white-space: nowrap;
	background: #e0e0e0;
	color: #424242;
}
.type-skill {
	background: #e3f2fd;
	color: #1565c0;
}
.type-competency {
	background: #f3e5f5;
	color: #7b1fa2;
}
.type-knowledge {
	background: #e8f5e9;
	color: #2e7d32;
}
.type-behaviour,
.type-behavior {
	background: #fff3e0;
	color: #e65100;
}
.skill-meta {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 12px;
	color: #757575;
}
.confidence-badge {
	display: inline-block;
	font-size: 11px;
	font-weight: 500;
	padding: 1px 6px;
	border-radius: 8px;
	background: #e8f5e9;
	color: #2e7d32;
}
.skill-id {
	font-family: monospace;
	font-size: 11px;
	color: #9e9e9e;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 180px;
	white-space: nowrap;
}
.skill-section {
	margin-top: 10px;
	padding-top: 10px;
	border-top: 1px solid #f0f0f0;
}
.section-title {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	color: #9e9e9e;
	margin-bottom: 6px;
}
.descriptor-text {
	font-size: 13px;
	line-height: 1.5;
	color: #424242;
	margin: 0;
}
.proficiency-grid {
	display: flex;
	flex-direction: column;
	gap: 8px;
}
.proficiency-item {
	display: flex;
	align-items: flex-start;
	gap: 10px;
}
.proficiency-badge {
	display: inline-block;
	font-size: 11px;
	font-weight: 600;
	padding: 2px 10px;
	border-radius: 10px;
	white-space: nowrap;
	flex-shrink: 0;
	min-width: 80px;
	text-align: center;
	background: #e0e0e0;
	color: #424242;
}
.level-0 {
	background: #e3f2fd;
	color: #1565c0;
}
.level-1 {
	background: #e8f5e9;
	color: #2e7d32;
}
.level-2 {
	background: #fff8e1;
	color: #f57f17;
}
.level-3 {
	background: #fce4ec;
	color: #c62828;
}
.level-4 {
	background: #f3e5f5;
	color: #7b1fa2;
}
.proficiency-description {
	font-size: 12px;
	line-height: 1.4;
	color: #616161;
	margin: 0;
}
</style>
